import { PositionCodeEvent } from '../core/types';

/**
 * 点击事件收集器
 * 监听带有 data-pc 属性的元素点击，记录来源位置码
 */
export class ClickCollector {
  private document: Document;
  private debug: boolean;
  private eventCallback: (event: PositionCodeEvent) => void;
  private clickHandler: ((e: MouseEvent) => void) | null = null;
  private currentPositionCode: string = '';
  private allowedDomains: string[] = [];
  private pendingPcCallback: ((pc: string) => void) | null = null;

  constructor(
    document: Document,
    eventCallback: (event: PositionCodeEvent) => void,
    allowedDomains: string[],
    pendingPcCallback: (pc: string) => void,
    debug: boolean = false
  ) {
    this.document = document;
    this.eventCallback = eventCallback;
    this.allowedDomains = allowedDomains;
    this.pendingPcCallback = pendingPcCallback;
    this.debug = debug;
  }

  /**
   * 设置当前页面的位置码（用于没有 data-pc 属性的链接）
   */
  setCurrentPositionCode(pc: string): void {
    this.currentPositionCode = pc;
    if (this.debug) {
      console.log('[MetricTracker] ClickCollector current position code set to:', pc);
    }
  }

  /**
   * 启动点击事件监听
   */
  start(): void {
    if (this.clickHandler) {
      if (this.debug) {
        console.log('[MetricTracker] Click collector already started');
      }
      return;
    }

    this.clickHandler = this.handleClick.bind(this);

    // 使用事件委托在 document 级别监听点击
    this.document.addEventListener('click', this.clickHandler, true);

    if (this.debug) {
      console.log('[MetricTracker] Click collector started');
    }
  }

  /**
   * 停止点击事件监听
   */
  stop(): void {
    if (this.clickHandler) {
      this.document.removeEventListener('click', this.clickHandler, true);
      this.clickHandler = null;

      if (this.debug) {
        console.log('[MetricTracker] Click collector stopped');
      }
    }
  }

  /**
   * 提取按钮文本（仅从 button 本身和 span 子元素中提取）
   */
  private extractButtonText(button: HTMLElement): string {
    let text = '';
    
    // 遍历按钮的子节点
    for (const node of Array.from(button.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        // 直接文本节点
        text += node.textContent?.trim() || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        // 只处理 span 元素
        if (element.tagName === 'SPAN') {
          text += element.textContent?.trim() || '';
        }
      }
    }
    
    return text.trim();
  }

  /**
   * 处理点击事件
   */
  private handleClick(e: MouseEvent): void {
    try {
      if (this.debug) {
        console.log('[MetricTracker] Click event detected, target:', e.target);
      }

      // 获取点击的目标元素
      let target = e.target as HTMLElement | null;
      const clickedElement = target; // 保存最初点击的元素

      // 向上查找带有 data-pc 或链接/按钮元素（一直找到为止）
      let linkElement: HTMLAnchorElement | null = null;
      let buttonElement: HTMLButtonElement | null = null;
      let pcValue: string | null = null;
      let pcFoundOnElement: HTMLElement | null = null; // 记录在哪个元素上找到的 PC
      let isIgnored = false;

      while (target) {
        // 检查是否设置了 data-pc-ignore
        if (target.getAttribute('data-pc-ignore') !== null) {
          isIgnored = true;
          if (this.debug) {
            console.log('[MetricTracker] Element has data-pc-ignore, skipping:', target);
          }
          break;
        }

        // 检查 data-pc 属性
        const dataPc = target.getAttribute('data-pc');

        if (this.debug && target === clickedElement) {
          console.log('[MetricTracker] Checking element:', target.tagName, 'data-pc:', dataPc, 'data-pc-ignore:', target.getAttribute('data-pc-ignore'));
        }

        if (dataPc) {
          pcValue = dataPc;
          pcFoundOnElement = target;
          if (this.debug) {
            console.log('[MetricTracker] Found data-pc:', pcValue, 'on element:', target);
          }
        }

        // 检查是否是链接元素（有 href 的 a 标签）
        if (!linkElement && target.tagName === 'A' && (target as HTMLAnchorElement).href) {
          linkElement = target as HTMLAnchorElement;
          if (this.debug) {
            console.log('[MetricTracker] Found link element:', linkElement);
          }
        }

        // 检查是否是 button 元素
        if (!buttonElement && target.tagName === 'BUTTON') {
          buttonElement = target as HTMLButtonElement;
          if (this.debug) {
            console.log('[MetricTracker] Found button element:', buttonElement);
          }
        }

        // 检查是否是无链接的 a 元素
        if (!linkElement && !buttonElement && target.tagName === 'A' && !(target as HTMLAnchorElement).href) {
          buttonElement = target as any; // 将无链接的 a 当作按钮处理
          if (this.debug) {
            console.log('[MetricTracker] Found anchor without href (treated as button):', buttonElement);
          }
        }

        // 如果找到了 pcValue，并且找到了链接或按钮，可以停止查找
        if (pcValue && (linkElement || buttonElement)) {
          break;
        }

        // 向上查找父元素
        target = target.parentElement;
      }

      // 如果被忽略，直接返回
      if (isIgnored) {
        return;
      }

      // 如果没有找到 data-pc，使用当前页面的位置码
      if (!pcValue) {
        pcValue = this.currentPositionCode;
        if (this.debug) {
          console.log('[MetricTracker] No data-pc found, using current position code:', pcValue);
        }
      }

      // 如果是 button 元素，并且在 button 上没有明确设置 data-pc，则提取按钮文本作为下一级
      if (buttonElement && pcValue && pcFoundOnElement !== buttonElement) {
        const buttonText = this.extractButtonText(buttonElement);
        if (buttonText) {
          pcValue = `${pcValue}.${buttonText}`;
          if (this.debug) {
            console.log('[MetricTracker] Button text appended to pc:', pcValue);
          }
        }
      }

      // 处理不同类型的点击
      if (linkElement && pcValue) {
        // 链接点击
        const willNavigateInCurrentWindow = this.handleLinkClick(e, linkElement, pcValue);

        if (willNavigateInCurrentWindow) {
          // 链接会在当前窗口跳转，事件将在新页面从 URL 参数中收集
          // 不在这里添加事件，避免重复
          if (this.pendingPcCallback) {
            this.pendingPcCallback(pcValue);
          }
        } else {
          // 外部链接、新窗口打开等情况，在当前页面记录事件
          const event: PositionCodeEvent = {
            sourcePc: pcValue,
            eventTime: new Date(),
          };
          this.eventCallback(event);
        }
      } else if (buttonElement && pcValue) {
        // 按钮或无链接的 a 元素点击，直接上报
        if (this.debug) {
          console.log('[MetricTracker] Button or anchor without href clicked, reporting event');
        }

        const event: PositionCodeEvent = {
          sourcePc: pcValue,
          eventTime: new Date(),
        };
        this.eventCallback(event);
      } else if (pcValue) {
        // 其他有 data-pc 的元素点击
        if (this.debug) {
          console.log('[MetricTracker] Element with data-pc clicked');
        }

        const event: PositionCodeEvent = {
          sourcePc: pcValue,
          eventTime: new Date(),
        };
        this.eventCallback(event);
      }
    } catch (error) {
      console.error('[MetricTracker] Error in click collector:', error);
    }
  }

  /**
   * 查找链接元素（在当前元素或其父元素中）
   */
  private findLinkElement(element: HTMLElement): HTMLAnchorElement | null {
    let current: HTMLElement | null = element;
    let depth = 0;
    const maxDepth = 5;

    if (this.debug) {
      console.log('[MetricTracker] Searching for link element starting from:', element.tagName);
    }

    while (current && depth < maxDepth) {
      if (this.debug) {
        console.log(`[MetricTracker] Depth ${depth}: checking element ${current.tagName}, href:`, (current as any).href);
      }

      if (current.tagName === 'A' && (current as HTMLAnchorElement).href) {
        if (this.debug) {
          console.log('[MetricTracker] Found link element:', current, 'href:', (current as HTMLAnchorElement).href);
        }
        return current as HTMLAnchorElement;
      }
      current = current.parentElement;
      depth++;
    }

    if (this.debug) {
      console.log('[MetricTracker] No link element found in hierarchy');
    }

    return null;
  }

  /**
   * 处理链接点击，添加 pc 参数
   * @returns 是否需要添加 pc 参数（返回 true 表示需要在路由变化后补充 pc）
   */
  private handleLinkClick(e: MouseEvent, link: HTMLAnchorElement, pcValue: string): boolean {
    try {
      const href = link.href;

      if (this.debug) {
        console.log('[MetricTracker] Processing link click:', {
          href,
          target: link.getAttribute('target'),
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          button: e.button,
        });
      }

      // 跳过外部链接（不同域名）
      if (!this.isSameDomain(href)) {
        if (this.debug) {
          console.log('[MetricTracker] Skipping external link:', href);
        }
        return false;
      }

      // 跳过特殊协议链接
      if (href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        if (this.debug) {
          console.log('[MetricTracker] Skipping special protocol link:', href);
        }
        return false;
      }

      // 跳过新窗口打开
      const target = link.getAttribute('target');
      if (target === '_blank' || e.ctrlKey || e.metaKey || e.button === 1) {
        if (this.debug) {
          console.log('[MetricTracker] Skipping new window link');
        }
        return false;
      }

      // 解析 URL
      const url = new URL(href);

      // 检查是否已经有 pc 参数
      const existingPc = url.searchParams.get('pc');

      if (existingPc) {
        if (this.debug) {
          console.log('[MetricTracker] Link already has pc parameter:', existingPc, 'skipping');
        }
        return false;
      }

      // 需要添加 pc 参数
      if (this.debug) {
        console.log('[MetricTracker] Will add pc parameter after route change:', pcValue);
      }

      // 同时也修改 href（对于非 SPA 页面）
      url.searchParams.set('pc', pcValue);
      link.href = url.toString();

      return true;  // 告诉 MetricTracker 需要在路由变化后补充 pc
    } catch (error) {
      console.error('[MetricTracker] Error handling link click:', error);
      return false;
    }
  }

  /**
   * 检查是否是允许的域名链接
   * 支持通配符匹配，如 *.laeni.cn
   */
  private isSameDomain(href: string): boolean {
    try {
      const url = new URL(href);
      const linkHostname = url.hostname;

      // 检查是否匹配任何允许的域名
      const isAllowed = this.allowedDomains.some(domain => {
        return this.matchDomain(linkHostname, domain);
      });

      if (this.debug) {
        console.log('[MetricTracker] Domain check:', {
          linkHostname,
          allowedDomains: this.allowedDomains,
          isAllowed,
        });
      }

      return isAllowed;
    } catch (error) {
      if (this.debug) {
        console.error('[MetricTracker] Error checking domain:', error);
      }
      return false;
    }
  }

  /**
   * 匹配域名（支持通配符）
   * @param hostname 要检查的域名
   * @param pattern 模式（支持 * 通配符）
   */
  private matchDomain(hostname: string, pattern: string): boolean {
    // 精确匹配
    if (hostname === pattern) {
      return true;
    }

    // 通配符匹配
    if (pattern.startsWith('*.')) {
      const suffix = pattern.substring(2); // 移除 *.
      // 匹配 suffix 本身或 .suffix 结尾
      return hostname === suffix || hostname.endsWith('.' + suffix);
    }

    return false;
  }
}
